import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date-only string (YYYY-MM-DD) into {year, month, day} without timezone shift.
 * Returns null if the string is not a valid date-only format.
 */
function parseDateOnly(dateString) {
  if (!dateString) return null;
  const str = String(dateString).split('T')[0]; // strip any time component
  const parts = str.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { year, month, day };
    }
  }
  return null;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format date as dd/mmm/yyyy (e.g., 14/Mar/2026)
 * Handles date-only strings (YYYY-MM-DD) without timezone conversion.
 */
export function formatDate(dateString, options = {}) {
  if (!dateString) return '';

  try {
    // For date-only strings, parse manually to avoid UTC→local day shift
    const parsed = parseDateOnly(dateString);
    if (parsed) {
      const day = String(parsed.day).padStart(2, '0');
      return `${day}/${MONTH_ABBR[parsed.month - 1]}/${parsed.year}`;
    }

    // For full datetime strings, use UTC methods to stay safe
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = MONTH_ABBR[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

/**
 * Format datetime as dd/mmm/yyyy HH:MM (e.g., 12/Jan/2026 14:30)
 * For full datetime strings; uses local time for display.
 */
export function formatDateTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, '0');
    const month = MONTH_ABBR[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return formatDate(dateString);
  } catch {
    return dateString;
  }
}

export function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return '';
  
  try {
    // Parse date-only string manually to avoid UTC→local day shift
    const parsed = parseDateOnly(dateOfBirth);
    if (parsed) {
      const today = new Date();
      let age = today.getFullYear() - parsed.year;
      const monthDiff = (today.getMonth() + 1) - parsed.month;
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.day)) {
        age--;
      }
      return age;
    }

    // Fallback for other formats
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  } catch {
    return '';
  }
}

export function getInitials(name) {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getCategoryColor(category) {
  const colors = {
    registration: 'bg-blue-100 text-blue-800',
    consultation: 'bg-sky-100 text-sky-800',
    progress: 'bg-green-100 text-green-800',
    prescription: 'bg-amber-100 text-amber-800',
    lab: 'bg-purple-100 text-purple-800',
    imaging: 'bg-pink-100 text-pink-800',
    discharge: 'bg-rose-100 text-rose-800',
    attachment: 'bg-slate-100 text-slate-800',
    clinical_note: 'bg-indigo-100 text-indigo-800',
    billing: 'bg-emerald-100 text-emerald-800',
    encounter: 'bg-cyan-100 text-cyan-800',
    appointment: 'bg-teal-100 text-teal-800',
    lab_results: 'bg-violet-100 text-violet-800',
    lab_document: 'bg-purple-100 text-purple-800',
  };
  
  return colors[category] || 'bg-slate-100 text-slate-800';
}

export function getCategoryIcon(category) {
  const icons = {
    registration: 'ClipboardList',
    consultation: 'Stethoscope',
    progress: 'FileText',
    prescription: 'Pill',
    lab: 'TestTube',
    imaging: 'Scan',
    discharge: 'LogOut',
    attachment: 'Paperclip',
  };
  
  return icons[category] || 'File';
}

export function getRoleColor(role) {
  const colors = {
    admin: 'bg-red-100 text-red-800',
    physician: 'bg-blue-100 text-blue-800',
    nurse: 'bg-green-100 text-green-800',
    clerk: 'bg-amber-100 text-amber-800',
    readonly: 'bg-slate-100 text-slate-800',
  };
  
  return colors[role] || 'bg-slate-100 text-slate-800';
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
